import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useConversation } from '@elevenlabs/react';
import { interviewStartMs, dueTimeCheck, timeCheckMessage } from '../utils/interviewTiming';
import { editorClosedMessage } from '../utils/liveCoding';
import { uploadWithRetry, xhrUpload, runEndSequence } from '../utils/videoUpload';
import { stripExpressionTags, isAgentSource } from '../utils/expressionTags';
import { createLayoutWatcher, readLayout } from '../utils/layoutWatch';
import { createChunkUploader, makeRecordingId } from '../utils/chunkedUpload';
import { loadModels, calibrate, startCameraAnalysis } from '../utils/cameraAnalysis/analyzer';
import { startTurnHold } from '../utils/turnHold';
import { createFinishFlow } from '../utils/finishFlow';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ═══════════════════════════════════════════════════════════════
// PHASE 1: PASSCODE ENTRY
// ═══════════════════════════════════════════════════════════════

function PasscodeEntry({ interviewId, onVerified }) {
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  // blockedMsg = terminal block (no retry). retryMsg = too early (can retry later).
  const [blockedMsg, setBlockedMsg] = useState('');
  const [retryMsg, setRetryMsg] = useState('');

  const handleSubmit = async () => {
    if (!passcode.trim()) { setError('Please enter your passcode'); return; }
    setIsVerifying(true);
    setError('');
    setBlockedMsg('');
    setRetryMsg('');

    try {
      const res = await fetch(
        `${API_BASE}/api/phase3/interview/${interviewId}/join?passcode=${encodeURIComponent(passcode.trim())}`
      );
      const data = await res.json();

      if (res.ok && data.status === 'success') {
        onVerified({ ...data.data, passcode: passcode.trim() });
      } else if (res.status === 401) {
        setError('Invalid passcode. Please check the code in your invitation email.');
      } else if (res.status === 400) {
        const msg = data.detail || '';
        // "try again in X minute(s)" = too early, retryable
        if (msg.toLowerCase().includes('try again in')) {
          setRetryMsg(msg);
        } else {
          // All other 400s are terminal — completed, in progress, window passed
          setBlockedMsg(msg);
        }
      } else {
        setError(data.detail || 'Unable to join. Please try again.');
      }
    } catch (e) {
      setError('Connection error. Please check your internet and try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  // ── TERMINAL BLOCK SCREEN — no form, no entry button ──
  if (blockedMsg) {
    const isCompleted = blockedMsg.toLowerCase().includes('completed') || blockedMsg.toLowerCase().includes('in progress');
    const isExpired   = blockedMsg.toLowerCase().includes('passed') || blockedMsg.toLowerCase().includes('reschedule');
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: isCompleted ? '#ecfdf5' : '#fef2f2' }}>
            <span className="text-4xl">{isCompleted ? '✅' : '⏰'}</span>
          </div>
          <h2 className="text-xl font-black text-gray-900 mb-2">
            {isCompleted ? 'Interview Already Completed' : 'Interview Window Closed'}
          </h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">{blockedMsg}</p>
          {isExpired && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <p className="text-amber-800 text-sm font-semibold mb-1">What to do next</p>
              <p className="text-amber-700 text-sm">Please contact your recruiter to reschedule the interview. They will send you a new invitation with a fresh time slot.</p>
            </div>
          )}
          <button
            onClick={() => window.close()}
            className="w-full py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-700 transition-all"
          >
            Close Window
          </button>
          <p className="text-xs text-gray-400 text-center mt-4">
            Powered by Quali5d • AI Technical Interview Platform
          </p>
        </div>
      </div>
    );
  }

  // ── NORMAL PASSCODE ENTRY SCREEN ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🔐</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Technical Interview</h1>
          <p className="text-gray-500 text-sm mt-1">Enter the passcode from your invitation email</p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Interview Passcode</label>
          <input
            type="text"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="e.g., A3X9B2K7"
            maxLength={12}
            className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-purple-500 focus:border-purple-500 uppercase"
            autoFocus
          />
        </div>

        {/* Too-early message — candidate can retry later */}
        {retryMsg && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-blue-800 font-semibold text-sm mb-1">⏳ Not yet open</p>
            <p className="text-blue-700 text-sm">{retryMsg}</p>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-center gap-2">
            <span>⚠️</span> {error}
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={isVerifying || !passcode.trim()}
          className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-lg rounded-xl hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
        >
          {isVerifying ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              Verifying...
            </span>
          ) : 'Enter Interview →'}
        </button>

        <p className="text-xs text-gray-400 text-center mt-4">
          Powered by Quali5d • AI Technical Interview Platform
        </p>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// PHASE 2: PRE-INTERVIEW LOBBY
// ═══════════════════════════════════════════════════════════════

export function PreInterviewLobby({ interviewData, onStartInterview }) {
  const [consent, setConsent] = useState(false);
  const [audioDevices, setAudioDevices] = useState({ inputs: [], outputs: [] });
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [selectedCamera, setSelectedCamera] = useState('');
  const [micTestActive, setMicTestActive] = useState(false);
  const [micLevel, setMicLevel] = useState(0);
  const [cameraPreview, setCameraPreview] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  // Deploy I: optional camera analysis + 5-s head-pose calibration
  const [cameraAnalysisConsent, setCameraAnalysisConsent] = useState(false);
  // Company setting from /join: off (no tick-box, no model download) / optional / required
  const cameraSetting = interviewData?.camera_analysis || { enabled: false, required: false };
  const [calibration, setCalibration] = useState({ status: 'idle', baseline: null });
  const runCalibration = async () => {
    setCalibration({ status: 'running', baseline: null });
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: selectedCamera ? { deviceId: { exact: selectedCamera } } : true, audio: false });
      const baseline = await calibrate(stream);
      setCalibration(baseline === null ? { status: 'failed', baseline: null } : { status: 'done', baseline });
    } catch (e) {
      setCalibration({ status: 'failed', baseline: null });
    } finally {
      if (stream) stream.getTracks().forEach(t => t.stop());
    }
  };

  const micTestRef = useRef(null);
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);

  useEffect(() => {
    enumerateDevices();
    return () => { stopMicTest(); stopCameraPreview(); };
  }, []);

  const enumerateDevices = async () => {
    try {
      // Permission prompt only (device labels need it) — release the devices straight away so the
      // camera isn't held open in the background while the candidate tests it
      const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      permissionStream.getTracks().forEach(t => t.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      const inputs = devices.filter(d => d.kind === 'audioinput');
      const outputs = devices.filter(d => d.kind === 'audiooutput');
      const cameras = devices.filter(d => d.kind === 'videoinput');
      setAudioDevices({ inputs, outputs });
      setVideoDevices(cameras);
      if (inputs.length > 0) setSelectedMic(inputs[0].deviceId);
      if (outputs.length > 0) setSelectedSpeaker(outputs[0].deviceId);
      if (cameras.length > 0) setSelectedCamera(cameras[0].deviceId);
    } catch (e) {
      if (e.name === 'NotAllowedError') {
        setDeviceError('Camera and microphone access is required for this interview. Please allow access in your browser settings.');
      } else {
        setDeviceError('Could not detect audio/video devices. Please check your connections.');
      }
    }
  };

  const startMicTest = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: selectedMic ? { exact: selectedMic } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      micTestRef.current = { stream, audioContext, interval: null };
      setMicTestActive(true);
      micTestRef.current.interval = setInterval(() => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setMicLevel(Math.min(100, Math.round(avg * 1.5)));
      }, 100);
    } catch (e) { setDeviceError('Microphone test failed. Please check your device.'); }
  };

  const stopMicTest = () => {
    if (micTestRef.current) {
      if (micTestRef.current.interval) clearInterval(micTestRef.current.interval);
      if (micTestRef.current.stream) micTestRef.current.stream.getTracks().forEach(t => t.stop());
      if (micTestRef.current.audioContext) micTestRef.current.audioContext.close();
      micTestRef.current = null;
    }
    setMicTestActive(false);
    setMicLevel(0);
  };

  const toggleCameraPreview = async () => {
    if (cameraPreview) { stopCameraPreview(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: selectedCamera ? { exact: selectedCamera } : undefined }
      });
      cameraStreamRef.current = stream;
      // The <video> only mounts once cameraPreview is true — the effect below attaches the stream then
      // (setting srcObject here hit a null ref, so the preview stayed black)
      setCameraPreview(true);
    } catch (e) { setDeviceError('Camera preview failed.'); }
  };

  useEffect(() => {
    if (cameraPreview && videoRef.current && cameraStreamRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [cameraPreview]);

  const stopCameraPreview = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(t => t.stop());
      cameraStreamRef.current = null;
    }
    setCameraPreview(false);
  };

  const canStart = consent && selectedMic && !deviceError && (!cameraSetting.required || cameraAnalysisConsent);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <header className="bg-gray-800/80 backdrop-blur border-b border-gray-700 px-6 py-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-lg font-bold text-white">AI Technical Interview</h1>
            <p className="text-sm text-gray-400">{interviewData.role_type} • {interviewData.tech_stack}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-white font-semibold">{interviewData.candidate_name}</p>
            <p className="text-xs text-gray-400">{interviewData.duration_minutes} minutes • {interviewData.interview_level}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Welcome Card */}
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          <h2 className="text-xl font-bold text-gray-900 mb-3">Welcome, {interviewData.candidate_name}!</h2>
          <p className="text-gray-600 text-sm mb-4">
            You are about to begin a {interviewData.duration_minutes}-minute AI-conducted technical interview.
            Please review the instructions below and set up your devices before starting.
          </p>

          {/* Instructions */}
          <div className="bg-blue-50 rounded-xl p-4 mb-4">
            <h3 className="font-bold text-blue-900 text-sm mb-2">📋 Before You Begin</h3>
            <ul className="text-sm text-blue-800 space-y-1.5">
              {(interviewData.instructions || []).map((inst, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">✓</span> {inst}
                </li>
              ))}
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">✓</span> Do not switch browser tabs during the interview
              </li>
            </ul>
          </div>

          {/* Interview Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-purple-600">{interviewData.duration_minutes} min</p>
              <p className="text-xs text-gray-500">Duration</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-purple-600 capitalize">{interviewData.interview_level}</p>
              <p className="text-xs text-gray-500">Level</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-purple-600">{interviewData.role_type}</p>
              <p className="text-xs text-gray-500">Role</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-purple-600 capitalize">{interviewData.coding_mode?.replace('_', ' ')}</p>
              <p className="text-xs text-gray-500">Format</p>
            </div>
          </div>
        </div>

        {/* Device Setup */}
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          <h3 className="font-bold text-gray-900 mb-4">🎧 Device Setup</h3>

          {deviceError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              ⚠️ {deviceError}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {/* Microphone */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <span>🎤</span> Microphone
              </label>
              <select value={selectedMic} onChange={(e) => { setSelectedMic(e.target.value); stopMicTest(); }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-purple-500">
                {audioDevices.inputs.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(0, 8)}`}</option>
                ))}
              </select>
              <div className="mt-2 flex items-center gap-3">
                <button onClick={micTestActive ? stopMicTest : startMicTest}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${
                    micTestActive ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                  {micTestActive ? '⏹ Stop' : '🔊 Test Mic'}
                </button>
                {micTestActive && (
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-100"
                      style={{ width: `${micLevel}%`, backgroundColor: micLevel < 30 ? '#22c55e' : micLevel < 70 ? '#f59e0b' : '#ef4444' }} />
                  </div>
                )}
              </div>
            </div>

            {/* Speaker */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <span>🔈</span> Speaker
              </label>
              <select value={selectedSpeaker} onChange={(e) => setSelectedSpeaker(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-purple-500">
                {audioDevices.outputs.length === 0 && <option value="">Default Speaker</option>}
                {audioDevices.outputs.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || `Speaker ${d.deviceId.slice(0, 8)}`}</option>
                ))}
              </select>
            </div>

            {/* Camera */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <span>📷</span> Camera
              </label>
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <select value={selectedCamera} onChange={(e) => { setSelectedCamera(e.target.value); stopCameraPreview(); }}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-purple-500">
                    {videoDevices.map(d => (
                      <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0, 8)}`}</option>
                    ))}
                  </select>
                  <button onClick={toggleCameraPreview}
                    className={`mt-2 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${
                      cameraPreview ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                    {cameraPreview ? '⏹ Stop Preview' : '📷 Test Camera'}
                  </button>
                </div>
                {cameraPreview && (
                  <div className="w-40 h-28 bg-black rounded-xl overflow-hidden border-2 border-purple-400">
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Consent */}
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 w-5 h-5 text-purple-600 rounded border-gray-300 focus:ring-purple-500" />
            <div>
              <p className="font-semibold text-gray-900 text-sm">I consent to the following:</p>
              <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                <li>• This interview will be audio and video recorded for evaluation purposes</li>
                <li>• Your camera must remain on throughout the interview</li>
                <li>• My browser activity (tab switches, focus loss) will be monitored</li>
                <li>• An AI interviewer will conduct this session — no human is present</li>
                <li>• My responses will be scored and shared with the hiring team</li>
                <li>• Recordings are retained for 90 days and then deleted</li>
              </ul>
            </div>
          </label>
          {/* Deploy I: camera analysis — part of the same consent block (wording + the "required" option and its
              accessibility alternative: pre-launch legal review). Hidden entirely when switched off. */}
          {cameraSetting.enabled && (
          <label className="flex items-start gap-3 cursor-pointer mt-4 pt-4 border-t border-gray-100" data-testid="camera-analysis-consent">
            <input type="checkbox" checked={cameraAnalysisConsent}
              onChange={(e) => { setCameraAnalysisConsent(e.target.checked); if (e.target.checked) Promise.resolve().then(loadModels).catch(() => {}); }}
              className="mt-1 w-5 h-5 text-purple-600 rounded border-gray-300 focus:ring-purple-500" />
            <div>
              <p className="font-semibold text-gray-900 text-sm">
                Camera analysis on my device {cameraSetting.required ? '(required by the employer)' : '(optional)'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                During the interview, my camera image is analysed on my own device to detect whether I leave the
                frame, another person appears, or a phone is visible. These analysis results (times only, no
                images) are recorded alongside the interview video my recruiter already receives.
              </p>
              {cameraAnalysisConsent && (
                <div className="mt-2 flex items-center gap-3">
                  <button type="button" onClick={runCalibration} disabled={calibration.status === 'running'}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-purple-100 text-purple-700 disabled:opacity-50">
                    {calibration.status === 'running' ? '⏳ Look at the screen…' : '🎯 Calibrate camera (5 s)'}
                  </button>
                  <span className="text-xs text-gray-500" data-testid="calibration-status">
                    {calibration.status === 'done' && '✓ Calibrated'}
                    {calibration.status === 'failed' && 'Calibration unavailable — it will calibrate automatically'}
                    {calibration.status === 'idle' && 'Look straight at your screen while it runs'}
                  </span>
                </div>
              )}
            </div>
          </label>
          )}
        </div>

        {/* Start Button */}
        <button
          onClick={() => { stopMicTest(); stopCameraPreview();
            onStartInterview(selectedMic, selectedSpeaker, selectedCamera,
                             { enabled: !!cameraSetting.enabled, consent: !!cameraSetting.enabled && cameraAnalysisConsent,
                               baselinePitch: calibration.baseline }); }}
          disabled={!canStart}
          className="w-full py-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-xl rounded-2xl hover:from-green-600 hover:to-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xl hover:shadow-2xl"
        >
          {canStart ? '🎬 Start Interview' : '☑️ Complete setup and consent to continue'}
        </button>
      </main>
    </div>
  );
}


// ── ConversationManager ─────────────────────────────────────────────────────
// Wrapped in React.memo so it NEVER re-renders — useConversation is called
// exactly once for the lifetime of the interview. When LiveInterviewRoom
// re-renders (editor state, timer, proctoring), React.memo blocks this
// component entirely — the ElevenLabs WebSocket is never disrupted.
const ConversationManager = React.memo(({ config, conversationRef, isSpeakingRef }) => {
  const conv = useConversation(config);
  conversationRef.current = conv;
  isSpeakingRef.current = conv.isSpeaking;
  return null;
}, () => true); // never re-render from prop changes — config is stable via useRef
// Note: useConversation internal state changes (isSpeaking) still trigger updates

// ═══════════════════════════════════════════════════════════════
// PHASE 3: LIVE INTERVIEW ROOM
// ═══════════════════════════════════════════════════════════════

function LiveInterviewRoom({ interviewId, interviewData, sessionData, selectedMic, selectedSpeaker, selectedCamera,
  cameraAnalysis, editorState, setEditorState, pendingEditorRef }) {
  const [callState, setCallState] = useState('connecting');
  const [error, setError] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  // Video upload after End: idle → uploading → done | failed (shown on the completion screen)
  const [videoUpload, setVideoUpload] = useState({ status: 'idle', percent: 0, retry: 0 });
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // Chat transcript
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const chatEndRef = useRef(null);
  const isChatOpenRef = useRef(false);

  // Refs
  const conversationIdRef = useRef(null);
  const isProcessingRef = useRef(false);
  const timerIntervalRef = useRef(null);
  const callStateRef = useRef('connecting');
  const elapsedRef = useRef(0);
  const lastTimeCheckRef = useRef(0);   // last 5-minute mark announced to Alex
  const warningShownRef = useRef(false);
  const softWrapShownRef = useRef(false);
  const autoEndShownRef = useRef(false);
  const autoEndTimeoutRef = useRef(null);

  // DOM refs for WebRTC-safe updates
  const timerDisplayRef = useRef(null);
  const warningBannerRef = useRef(null);
  const softWrapBannerRef = useRef(null);
  const autoEndBannerRef = useRef(null);

  // Proctoring
  const proctoringStartRef = useRef(Date.now());
  const tabSwitchCountRef = useRef(0);          // count rapid tab switches
  const tabSwitchTimerRef = useRef(null);        // window for counting rapid switches
  const speakerObserverRef = useRef(null);       // MutationObserver for dynamic speaker assignment
  const micSilenceTimerRef = useRef(null);       // detect prolonged mic silence
  const audioAnalyserRef = useRef(null);         // Web Audio analyser node
  const audioContextRef = useRef(null);          // Web Audio context for silence detection
  const [networkOffline, setNetworkOffline] = useState(false);
  const [proctoringWarning, setProctoringWarning] = useState('');

  // ── Tool execution state ──

  // ── Live coding refs — needed for stale-closure-safe reads inside clientTools ──
  const editorCodeRef = useRef('');
  const editorLanguageRef = useRef('python');
  const codeSubmittedRef = useRef(false);

  // ── Video Recording (MediaRecorder) ──
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const cameraStreamRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const streamReadyRef = useRef(false); // signals proctoring once camera stream is available
  // Deploy F: chunks upload during the interview; offset = recorder start − call connect (A/V merge)
  const chunkUploaderRef = useRef(null);
  const recordingIdRef = useRef(null);
  const connectAtRef = useRef(null);
  const recordStartAtRef = useRef(null);
  const passcodeRef = useRef(interviewData?.passcode || '');
  // Deploy I: camera analysis (consent + calibration from the lobby; set once, read via ref)
  const cameraAnalysisRef = useRef(cameraAnalysis || { consent: false, baselinePitch: null });
  const analyzerRef = useRef(null);
  useEffect(() => { passcodeRef.current = interviewData?.passcode || ''; }, [interviewData]);
  const conversationRef = useRef(null);  // set by ConversationManager — stable conversation object
  const isSpeakingRef  = useRef(false);  // set by ConversationManager — current isSpeaking value
  // Bundle K: turn hold on the candidate's mic, server-enforced finish, auto-end after an allowed closing
  const micStreamRef = useRef(null);
  const turnHoldRef = useRef(null);
  const finishFlowRef = useRef(null);
  const handleEndRef = useRef(null);     // latest handleEndInterview (the call config is built once)

  // ── Live Coding Editor ──
  // editorState + pendingEditorRef are PROPS from CandidateInterviewPage (parent).
  // setEditorState fires in the parent → only parent re-renders.
  // LiveInterviewRoom + ConversationManager never re-render → WebSocket stays alive.
  const editorOpen         = editorState.open;
  const editorCode         = editorState.code;
  const editorLanguage     = editorState.language;
  const editorTimeRemaining = editorState.timeRemaining;
  const codeSubmitted      = editorState.submitted;
  // Helper setters — keep old call-sites unchanged
  const setEditorOpen     = (v) => setEditorState(s => ({ ...s, open: v }));
  const setEditorCode     = (v) => setEditorState(s => ({ ...s, code: typeof v === 'function' ? v(s.code) : v }));
  const setEditorLanguage = (v) => setEditorState(s => ({ ...s, language: v }));
  const setEditorTimeRemaining = (v) => setEditorState(s => ({ ...s, timeRemaining: typeof v === 'function' ? v(s.timeRemaining) : v }));
  const setCodeSubmitted  = (v) => setEditorState(s => ({ ...s, submitted: v }));
  // editorTimeLimit stored as a ref — tracks the limit set by open_code_editor tool
  const editorTimeLimitRef = React.useRef(0);
  const editorTimerRef = useRef(null);
  const codingStartedRef = useRef(false);

  useEffect(() => { isChatOpenRef.current = isChatOpen; }, [isChatOpen]);
  useEffect(() => { editorCodeRef.current = editorCode; }, [editorCode]);
  useEffect(() => { editorLanguageRef.current = editorLanguage; }, [editorLanguage]);
  useEffect(() => { codeSubmittedRef.current = codeSubmitted; }, [codeSubmitted]);
  useEffect(() => {
    if (isChatOpen && chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isChatOpen]);
  useEffect(() => { if (isChatOpen) setUnreadCount(0); }, [isChatOpen]);

  const addChatMessage = useCallback((source, message) => {
    // "[smile]"-style voice directions in Alex's text are not words — never shown (candidate text untouched)
    if (isAgentSource(source)) message = stripExpressionTags(message);
    if (!message) return;
    setChatMessages(prev => [...prev, { id: Date.now() + Math.random(), source, message, timestamp: new Date() }]);
    if (!isChatOpenRef.current) setUnreadCount(prev => prev + 1);
  }, []);

  // ── Send proctoring signal ──
  const sendProctoringSignal = useCallback((signalType, details = '') => {
    const elapsed = (Date.now() - proctoringStartRef.current) / 1000;
    const severityMap = {
      tab_switch: 'high', focus_loss: 'medium', mic_muted: 'high',
      mic_disconnected: 'high', camera_off: 'high', camera_disconnected: 'high',
      copy_attempt: 'medium', paste_attempt: 'medium', fullscreen_exit: 'medium',
      mic_silence: 'medium', viewport_shrank: 'low', viewport_restored: 'low', rapid_tab_switches: 'high',
      phone_detected: 'medium', looking_down: 'low', face_absent: 'low', multiple_faces: 'medium',
      camera_analysis_declined: 'low', analysis_degraded: 'low',
      network_offline: 'high', network_restored: 'low',
      recording_error: 'low', camera_error: 'low',
    };
    fetch(`${API_BASE}/api/phase3/interview/${interviewId}/proctoring-signal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        signal_type: signalType,
        timestamp_seconds: elapsed,
        severity: severityMap[signalType] || 'low',
        details
      })
    }).catch(e => console.warn('Proctoring signal failed:', e));
  }, [interviewId]);

  // ── Proctoring: full suite ──
  useEffect(() => {
    if (callState !== 'connected') return;
    proctoringStartRef.current = Date.now();

    // 1. Tab switch — count rapid switches
    const handleVisibilityChange = () => {
      if (document.hidden) {
        tabSwitchCountRef.current += 1;
        sendProctoringSignal('tab_switch', `Tab hidden (count: ${tabSwitchCountRef.current})`);
        setProctoringWarning('⚠️ Tab switching detected. Please stay on this page.');
        setTimeout(() => setProctoringWarning(''), 4000);
        // Flag rapid switching (3+ times in 2 minutes)
        if (!tabSwitchTimerRef.current) {
          tabSwitchTimerRef.current = setTimeout(() => {
            tabSwitchCountRef.current = 0;
            tabSwitchTimerRef.current = null;
          }, 120000);
        }
        if (tabSwitchCountRef.current >= 3) {
          sendProctoringSignal('rapid_tab_switches', `${tabSwitchCountRef.current} switches in 2 minutes`);
        }
      }
    };

    // 2. Window focus loss
    const handleBlur = () => {
      sendProctoringSignal('focus_loss', 'Window lost focus');
    };

    // 3. Copy/paste detection
    const handleCopy = () => {
      sendProctoringSignal('copy_attempt', 'Candidate copied text');
    };
    const handlePaste = () => {
      sendProctoringSignal('paste_attempt', 'Candidate pasted text');
    };

    // 4. Fullscreen monitoring — detect exit only (browsers block auto-request without user gesture)
    const handleFullscreenChange = () => {
      if (document.fullscreenElement === null) {
        sendProctoringSignal('fullscreen_exit', 'Exited fullscreen during interview');
        setProctoringWarning('⚠️ Please stay in fullscreen mode during the interview.');
        setTimeout(() => setProctoringWarning(''), 5000);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // 5. Page-layout changes (replaces the "DevTools" size check — see utils/layoutWatch.js). Baseline at the
    //    start; one signal when the page shrinks inside the window and one when it is restored. Info only.
    const layoutInterval = setInterval(createLayoutWatcher(() => readLayout(window), sendProctoringSignal), 5000);

    // 6. Network offline/online detection
    const handleOffline = () => {
      setNetworkOffline(true);
      sendProctoringSignal('network_offline', 'Candidate internet connection lost');
    };
    const handleOnline = () => {
      setNetworkOffline(false);
      sendProctoringSignal('network_restored', 'Candidate internet connection restored');
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      clearInterval(layoutInterval);
      if (tabSwitchTimerRef.current) { clearTimeout(tabSwitchTimerRef.current); tabSwitchTimerRef.current = null; }
    };
  }, [callState, sendProctoringSignal]);

  // ── Proctoring: mic silence detection ──
  // Track monitoring (camera_off, mic_muted etc.) is handled inside
  // startVideoRecording() after the stream is confirmed ready.
  useEffect(() => {
    if (callState !== 'connected') return;
    // Bundle K: the microphone stream the page keeps for the turn hold. It used to read the CAMERA stream,
    // which is opened with audio: false — so this check never ran.
    const stream = micStreamRef.current;
    if (!stream || !stream.getAudioTracks || stream.getAudioTracks().length === 0) return;

    // Mic silence detection — flag if silent for >45 seconds
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;
      const analyser = audioCtx.createAnalyser();
      audioAnalyserRef.current = analyser;
      analyser.fftSize = 256;
      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      let silenceStart = null;
      const SILENCE_THRESHOLD = 5;     // volume below this = silence
      const SILENCE_DURATION_MS = 45000; // 45 seconds
      micSilenceTimerRef.current = setInterval(() => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        if (avg < SILENCE_THRESHOLD) {
          if (!silenceStart) silenceStart = Date.now();
          else if (Date.now() - silenceStart > SILENCE_DURATION_MS) {
            sendProctoringSignal('mic_silence', `Microphone silent for ${Math.round((Date.now()-silenceStart)/1000)}s`);
            silenceStart = null; // reset so we don't spam
          }
        } else {
          silenceStart = null;
        }
      }, 2000);
    } catch (e) { console.warn('Silence detection unavailable:', e); }

    return () => {
      if (micSilenceTimerRef.current) clearInterval(micSilenceTimerRef.current);
      try { if (audioContextRef.current) audioContextRef.current.close(); } catch (e) {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callState]);

  // ── Video Recording: Start/Stop/Upload ──

  const startVideoRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: selectedCamera ? { deviceId: { exact: selectedCamera } } : true,
        audio: false   // Audio captured by ElevenLabs separately — avoid mic conflict
      });
      cameraStreamRef.current = stream;
      streamReadyRef.current = true;

      // ── Attach track monitoring here — stream is now ready ──
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onmute = () => {
          sendProctoringSignal('camera_off', 'Video track muted — camera turned off');
          setProctoringWarning('⚠️ Camera turned off. Please keep your camera on.');
          setTimeout(() => setProctoringWarning(''), 5000);
        };
        videoTrack.onended = () => {
          sendProctoringSignal('camera_disconnected', 'Video track ended — camera disconnected');
          setProctoringWarning('⚠️ Camera disconnected. Please reconnect.');
        };
      }
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onmute = () => {
          sendProctoringSignal('mic_muted', 'Audio track muted — microphone muted');
          setProctoringWarning('⚠️ Microphone muted. Please unmute to continue.');
          setTimeout(() => setProctoringWarning(''), 5000);
        };
        audioTrack.onended = () => {
          sendProctoringSignal('mic_disconnected', 'Audio track ended — microphone disconnected');
          setProctoringWarning('⚠️ Microphone disconnected. Please reconnect.');
        };
      }

      // Show live preview
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }

      // Determine supported mimeType
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
          ? 'video/webm;codecs=vp8'
          : MediaRecorder.isTypeSupported('video/webm')
            ? 'video/webm'
            : 'video/mp4';

      recordedChunksRef.current = [];
      const recorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 500000   // 500kbps — keeps files manageable for 60min interviews
      });

      // Deploy F: upload each 10-s chunk as it arrives (refs only — no React state during the call)
      const recordingId = makeRecordingId();
      recordingIdRef.current = recordingId;
      const passcode = passcodeRef.current;
      const postForm = async (path, fields, file) => {
        const form = new FormData();
        Object.entries(fields).forEach(([k, v]) => form.append(k, v));
        if (file) form.append('chunk', file, 'chunk');
        try {
          const res = await fetch(`${API_BASE}/api/phase3/interview/${interviewId}/${path}`, { method: 'POST', body: form });
          let body = {};
          try { body = await res.json(); } catch (e) { /* not JSON */ }
          return { ok: res.ok, status: res.status, error: res.ok ? '' : `HTTP ${res.status}`,
                   missing: body?.detail?.missing };
        } catch (e) {
          return { ok: false, status: 0, error: 'network error' };
        }
      };
      chunkUploaderRef.current = createChunkUploader({
        sendChunk: (index, blob) => postForm('video-chunk', { index, recording_id: recordingId, passcode }, blob),
        sendCommit: (count) => {
          const fields = { count, recording_id: recordingId, passcode, content_type: mimeType };
          if (recordStartAtRef.current && connectAtRef.current) {
            fields.video_offset_ms = recordStartAtRef.current - connectAtRef.current;
          }
          return postForm('video-commit', fields);
        },
      });
      recorder.onstart = () => { recordStartAtRef.current = Date.now(); };

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
          if (chunkUploaderRef.current) chunkUploaderRef.current.add(e.data);
        }
      };

      recorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        sendProctoringSignal('recording_error', `MediaRecorder error: ${e.error?.name || 'unknown'}`);
      };

      recorder.start(10000);   // Capture in 10-second chunks
      mediaRecorderRef.current = recorder;

      // Deploy I: second-device detection on the same stream (only episode times are sent)
      if (!analyzerRef.current) {
        if (!cameraAnalysisRef.current.enabled) {
          analyzerRef.current = { stop: () => {} };       // switched off for this company: no models, no signals
        } else if (cameraAnalysisRef.current.consent) {
          analyzerRef.current = startCameraAnalysis({
            stream, send: sendProctoringSignal, baselinePitch: cameraAnalysisRef.current.baselinePitch,
            elapsedSeconds: () => (Date.now() - proctoringStartRef.current) / 1000 });
        } else {
          analyzerRef.current = { stop: () => {} };
          sendProctoringSignal('camera_analysis_declined', 'The candidate did not opt in to camera analysis');
        }
      }
      console.log(`✅ Video recording started (${mimeType})`);

    } catch (e) {
      console.error('Failed to start video recording:', e);
      // Don't block the interview — proctoring signal only
      sendProctoringSignal('camera_error', `Camera recording failed: ${e.message}`);
    }
  }, [selectedCamera, sendProctoringSignal, interviewId]);   // passcode via ref: the recorder must never restart

  const stopVideoRecording = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        // Stop camera stream
        if (cameraStreamRef.current) {
          cameraStreamRef.current.getTracks().forEach(t => t.stop());
          cameraStreamRef.current = null;
        }
        resolve(null);
        return;
      }

      recorder.onstop = () => {
        // Stop camera stream
        if (cameraStreamRef.current) {
          cameraStreamRef.current.getTracks().forEach(t => t.stop());
          cameraStreamRef.current = null;
        }

        const chunks = recordedChunksRef.current;
        if (chunks.length === 0) {
          console.warn('No video chunks recorded');
          resolve(null);
          return;
        }

        const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
        console.log(`📹 Video recording stopped: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
        resolve(blob);
      };

      recorder.stop();
    });
  }, []);

  // Upload with progress + up to 3 retries. If every attempt fails, record a
  // video_upload_failed signal so the HM review explains the missing video.
  const uploadVideoBlob = useCallback(async (videoBlob) => {
    if (!videoBlob || videoBlob.size < 1000) {
      console.warn('Video blob too small or empty, skipping upload');
      return;
    }
    const sizeMb = (videoBlob.size / 1024 / 1024).toFixed(1);
    const ext = videoBlob.type.includes('mp4') ? 'mp4' : 'webm';
    const passcode = interviewData?.passcode || '';
    setVideoUpload({ status: 'uploading', percent: 0, retry: 0 });

    // Deploy F: most chunks were uploaded during the interview — send the rest and commit
    const uploader = chunkUploaderRef.current;
    if (uploader) {
      const chunked = await uploader.finish((percent) => setVideoUpload({ status: 'uploading', percent, retry: 0 }));
      if (chunked.ok) {
        console.log(`✅ Video uploaded in ${chunked.chunks} chunks (${sizeMb} MB)`);
        setVideoUpload({ status: 'done', percent: 100, retry: 0 });
        return;
      }
      console.warn(`Chunked video upload unavailable (${chunked.reason}) — uploading the whole file instead`);
      setVideoUpload({ status: 'uploading', percent: 0, retry: 0 });
    }
    const offsetMs = recordStartAtRef.current && connectAtRef.current
      ? recordStartAtRef.current - connectAtRef.current : null;

    const result = await uploadWithRetry((attempt) => {
      const formData = new FormData();
      formData.append('video_file', videoBlob, `interview_${interviewId}.${ext}`);
      formData.append('passcode', passcode);
      if (offsetMs !== null) formData.append('video_offset_ms', String(offsetMs));
      return xhrUpload(`${API_BASE}/api/phase3/interview/${interviewId}/upload-video`, formData,
        (percent) => setVideoUpload({ status: 'uploading', percent, retry: attempt }));
    }, {
      onRetry: (attempt, last) => {
        console.warn(`Video upload failed (${last?.error}) — retry ${attempt}`);
        setVideoUpload({ status: 'uploading', percent: 0, retry: attempt });
      },
    });

    if (result.ok) {
      console.log(`✅ Video uploaded (${sizeMb} MB, ${result.attempts} attempt(s))`);
      setVideoUpload({ status: 'done', percent: 100, retry: 0 });
      return;
    }
    console.error('❌ Video upload failed after all attempts:', result.last?.error);
    setVideoUpload({ status: 'failed', percent: 0, retry: 0 });
    try {
      await fetch(`${API_BASE}/api/phase3/interview/${interviewId}/proctoring-signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signal_type: 'video_upload_failed',
          severity: 'low',
          passcode,
          details: `Video upload failed after ${result.attempts} attempt(s): ${result.last?.error || 'unknown error'} `
            + `(${sizeMb} MB ${ext})`,
        }),
      });
    } catch (e) {
      console.warn('Could not record video_upload_failed signal:', e);
    }
  }, [interviewId, interviewData]);

  // Warn before the page closes while the recording is still uploading
  useEffect(() => {
    if (videoUpload.status !== 'uploading') return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [videoUpload.status]);

  // Auto-start video recording when ElevenLabs connects
  useEffect(() => {
    if (callState === 'connected') {
      startVideoRecording();
    }
  }, [callState, startVideoRecording]);

  // ── ElevenLabs conversation ──
  // ── CRITICAL: Stable references for useConversation ──────────────────────
  // Each value passed to useConversation must have a STABLE reference across
  // renders. If any reference changes, ElevenLabs hook tears down and rebuilds
  // the WebSocket — dropping the live interview mid-session.
  //
  // useMemo([], []) = created once, never recreated
  // ── PERMANENT CONFIG REF ─────────────────────────────────────────────────
  // useRef guarantees the config object NEVER changes identity — unlike useMemo
  // which React may recreate. Built once on first render; all dynamic values
  // are read from refs at call time, never captured as stale closures.
  // This is the definitive fix for ConversationManager re-mounting on re-render.
  const conversationConfigRef = useRef(null);
  if (!conversationConfigRef.current) {
    conversationConfigRef.current = {
      clientTools: {
        open_code_editor: async ({ language, time_limit_seconds }) => {
          console.log('[Tool Called] open_code_editor', { language, time_limit_seconds });
          try {
            // Normalise language — ElevenLabs agent may send 'csharp' instead of 'c#'
            const normalise = (l) => (l || '').toLowerCase().replace('csharp', 'c#').replace('c sharp', 'c#');
            const validLanguages = ['python', 'javascript', 'java', 'c++', 'c#', 'go', 'sql', 'other'];
            const lang = validLanguages.includes(normalise(language)) ? normalise(language) : 'python';
            const limit = Math.max(60, Math.min(time_limit_seconds || 600, 3600));
            editorTimeLimitRef.current = limit;  // store limit so notification reads correct value
            codingStartedRef.current = false;    // reset so typing-start fires again on second open
            const result = {
              success: true,
              message: `Editor opened. Language: ${lang}. Timer: ${Math.round(limit / 60)} minutes. `
                + `The candidate is now writing code. `
                + `Stay silent and wait. `
                + `You will automatically receive a [SYSTEM] message with their code when they submit or time expires — `
                + `the editor then closes.`
            };
            console.log('[Tool Result] open_code_editor', result);
            pendingEditorRef.current = { lang, limit, requestedAt: Date.now() };
            return result;
          } catch (error) {
            console.error('[Tool Error] open_code_editor:', error);
            return { success: false, error: error.message };
          }
        },
        get_submitted_code: async () => {
          console.log('[Tool Called] get_submitted_code');
          try {
            const code = editorCodeRef.current || '(no code written)';
            const result = {
              code,
              language: editorLanguageRef.current,
              submitted: codeSubmittedRef.current,
              instruction: code === '(no code written)'
                ? 'The candidate did not write any code. Ask them verbally to explain their approach and what they would have written. '
                  + 'The editor is closed — call open_code_editor again only if you ask them to write code.'
                : `You now have the candidate's ${editorLanguageRef.current} code. `
                  + 'READ IT CAREFULLY before speaking. Then say: "Thank you — let\'s walk through your solution together." '
                  + 'Ask SPECIFIC questions based on what you actually see in the code — reference specific lines, variable names, or patterns you notice. '
                  + 'Cover these areas: 1) Their overall approach and why they chose it. '
                  + '2) Time and space complexity of their actual implementation. '
                  + '3) Edge cases their current code may not handle. '
                  + '4) How they would optimise it. '
                  + 'If they submitted early with time remaining, acknowledge that positively. '
                  + 'Ask these questions verbally one at a time. '
                  + 'The editor is closed — call open_code_editor again before asking for more code.'
            };
            console.log('[Tool Result] get_submitted_code', { language: result.language, submitted: result.submitted, codeLength: code.length });
            return result;
          } catch (error) {
            console.error('[Tool Error] get_submitted_code:', error);
            return { code: '(error reading code)', language: 'unknown', submitted: false };
          }
        }
      },
      onConnect: ({ conversationId: connId }) => {
        console.log('✅ Phase 3: Connected to AI interviewer');
        if (connectAtRef.current === null) connectAtRef.current = Date.now();   // the call audio starts here
        setCallState('connected');
        callStateRef.current = 'connected';
        if (connId) conversationIdRef.current = connId;
      },
      onDisconnect: () => {
        if (isProcessingRef.current || callStateRef.current === 'ended') return;
        console.log('🔌 Phase 3: Disconnected');
        callStateRef.current = 'error'; // update ref immediately so guards stop firing
        sendProctoringSignal('connection_lost', 'ElevenLabs WebSocket disconnected');
        setError('The connection to your AI interviewer was lost. Your progress has been saved. Please click Reconnect to continue — note the interview will resume from the beginning.');
        setCallState('error');
      },
      onError: (err) => {
        console.error('❌ Phase 3 error:', err);
        if (!isProcessingRef.current) {
          callStateRef.current = 'error';
          setError('Connection to AI interviewer was lost. The interview may still be saved.');
          setCallState('error');
        }
      },
      onMessage: (msg) => {
        if (msg?.source && msg?.message) addChatMessage(msg.source, msg.message);
        try {
          if (!msg?.message) return;
          if (isAgentSource(msg.source)) finishFlowRef.current?.onAgentText(msg.message);
          else finishFlowRef.current?.onUserText(msg.message);
        } catch (e) { /* never break the call */ }
      }
    };
  }

  // Bundle K: Alex starts to finish → the backend decides (reserve / verification first when >10 min left);
  // an allowed closing ends the call here once Alex goes quiet. Built once, reads refs only.
  if (!finishFlowRef.current) {
    finishFlowRef.current = createFinishFlow({
      check: async (payload) => {
        const res = await fetch(`${API_BASE}/api/phase3/interview/${interviewId}/finish-check`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ passcode: passcodeRef.current, ...payload }),
        });
        if (!res.ok) throw new Error(`finish-check HTTP ${res.status}`);
        return res.json();
      },
      sendInstruction: (text) => { if (callStateRef.current === 'connected') conversationRef.current?.sendContextualUpdate(text); },
      endCall: (reason) => handleEndRef.current?.(reason),
      isAgentSpeaking: () => !!isSpeakingRef.current,
      elapsedSeconds: () => elapsedRef.current,
      wrapUpStarted: () => warningShownRef.current,
    });
  }

  // conversation always read via conversationRef.current at call time — never at render time

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  const formatChatTime = (d) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

  // ── Start ElevenLabs session ──
  useEffect(() => {
    let started = false;
    const startSession = async () => {
      if (started) return;
      started = true;
      try {
        // Verify mic permission first — ElevenLabs opens its own stream internally.
        // Pass inputDeviceId so ElevenLabs uses the candidate's selected mic device.
        // Bundle K: kept (it used to be discarded) — the turn hold listens to it for the candidate's voice
        micStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            ...(selectedMic ? { deviceId: { exact: selectedMic } } : {}),
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            suppressLocalAudioPlayback: true,  // Chrome 109+: prevents speaker echo
          }
        });
        const conv = conversationRef.current;
        if (!conv) throw new Error('ConversationManager not ready — ref is null');
        const convId = await conv.startSession({
          signedUrl: sessionData.signed_url,
          inputDeviceId: selectedMic || undefined,
          outputDeviceId: selectedSpeaker || undefined,
          overrides: sessionData.system_prompt ? {
            agent: {
              prompt: { prompt: sessionData.system_prompt }
            }
          } : undefined,
          // clientTools registered in useConversation() above — not here
        });
        if (convId && !conversationIdRef.current) conversationIdRef.current = convId;

        // Set speaker — use MutationObserver to catch ElevenLabs audio elements added dynamically
        if (selectedSpeaker) {
          // Apply to any existing audio elements immediately
          document.querySelectorAll('audio').forEach(el => {
            if (el.setSinkId && el.sinkId !== selectedSpeaker) el.setSinkId(selectedSpeaker).catch(() => {});
          });
          // Watch for audio elements added dynamically by ElevenLabs SDK
          const speakerObserver = new MutationObserver(() => {
            document.querySelectorAll('audio').forEach(el => {
              if (el.setSinkId && el.sinkId !== selectedSpeaker) el.setSinkId(selectedSpeaker).catch(() => {});
            });
          });
          speakerObserver.observe(document.body, { childList: true, subtree: true });
          // Store observer ref for cleanup on unmount
          speakerObserverRef.current = speakerObserver;
        }
      } catch (e) {
        console.error('Session start error:', e);
        setError(e.message?.includes('permission')
          ? 'Microphone access denied.'
          : 'Unable to start interview. Please refresh and try again.');
        setCallState('error');
      }
    };
    startSession();
    return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Timer — uses Date.now() to prevent setInterval drift over long interviews ──
  useEffect(() => {
    if (callState === 'connected') {
      // Anchor: the moment the call connected (now); the server's start time only when resuming
      const startTime = interviewStartMs(sessionData, sessionData?._receivedAtMs,
                                         Date.now() - (elapsedRef.current * 1000));
      timerIntervalRef.current = setInterval(() => {
        const t = Math.floor((Date.now() - startTime) / 1000);
        elapsedRef.current = t;
        setElapsedTime(t);
        if (timerDisplayRef.current) timerDisplayRef.current.textContent = `⏱️ ${formatTime(t)}`;
      }, 1000);
    }
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (micSilenceTimerRef.current) clearInterval(micSilenceTimerRef.current);
      if (autoEndTimeoutRef.current) clearTimeout(autoEndTimeoutRef.current);
      if (speakerObserverRef.current) { speakerObserverRef.current.disconnect(); speakerObserverRef.current = null; }
    };
  }, [callState, sessionData]);   // sessionData is set once before the call and never changes during it

  // ── Bundle K: turn hold — while the candidate speaks (or just did), tell ElevenLabs they are active ──
  useEffect(() => {
    if (callState !== 'connected' || turnHoldRef.current) return;
    turnHoldRef.current = startTurnHold({
      stream: micStreamRef.current,
      send: () => conversationRef.current?.sendUserActivity?.(),
      isAgentSpeaking: () => !!isSpeakingRef.current,
    });
  }, [callState]);
  useEffect(() => () => {               // unmount: release the hold, the finish timer and the mic
    try { turnHoldRef.current?.stop(); } catch (e) { /* ignore */ }
    try { finishFlowRef.current?.stop(); } catch (e) { /* ignore */ }
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
  }, []);

  // ── Time-based triggers ──
  useEffect(() => {
    if (callState !== 'connected') return;
    // The schedule drives the timer (never the blueprint's own estimate)
    const dur = (interviewData.duration_minutes || sessionData.blueprint_summary?.duration_minutes || 60) * 60;
    const firstName = (interviewData.candidate_name || '').split(' ')[0] || 'there';

    // ── Every 5 minutes until T-5: silent time check so Alex can pace (it has no clock) ──
    const mark = dueTimeCheck(elapsedTime, dur, lastTimeCheckRef.current);
    if (mark !== null) {
      lastTimeCheckRef.current = mark;
      try {
        conversationRef.current?.sendContextualUpdate(
          timeCheckMessage(elapsedTime, dur, sessionData.blueprint_summary?.problem_count)
        );
      } catch (e) {}
    }

    // ── T-5 minutes: AI starts wrapping up ──
    // This is the KEY trigger — AI needs to know NOW, not when time is already up
    if (elapsedTime >= dur - 300 && !warningShownRef.current) {
      warningShownRef.current = true;
      if (warningBannerRef.current) warningBannerRef.current.classList.remove('hidden');
      if (timerDisplayRef.current) timerDisplayRef.current.classList.add('text-orange-400', 'font-semibold');
      try {
        conversationRef.current?.sendContextualUpdate(
          `You have approximately 5 minutes remaining in this interview. ` +
          `Begin wrapping up naturally — complete your current question but do not start any new ones. ` +
          `Once the current question is finished, thank ${firstName} warmly and let them know that a recruiter will be in touch ` +
          `with feedback soon. End the interview gracefully within the next few minutes, finishing with: ` +
          `"You can click End Interview whenever you're ready."`
        );
      } catch (e) {}
    }

    // ── T+0: Scheduled end — firm close if AI hasn't ended yet ──
    if (elapsedTime >= dur && !softWrapShownRef.current) {
      softWrapShownRef.current = true;
      if (warningBannerRef.current) warningBannerRef.current.classList.add('hidden');
      if (softWrapBannerRef.current) softWrapBannerRef.current.classList.remove('hidden');
      if (timerDisplayRef.current) {
        timerDisplayRef.current.classList.remove('text-orange-400');
        timerDisplayRef.current.classList.add('text-orange-500', 'font-bold');
      }
      try {
        conversationRef.current?.sendContextualUpdate(
          `Time is up. If you haven't already, wrap up immediately. ` +
          `Say something like: "That brings us to the end of our interview, ${firstName}. ` +
          `Thank you so much for your time today — it was a pleasure speaking with you. ` +
          `One of our recruiters will be in touch with feedback very soon. ` +
          `We wish you all the best! You can click End Interview whenever you're ready." Then stop asking questions.`
        );
      } catch (e) {}
    }

    // ── T+90s: Hard end — AI has had enough time to deliver closing ──
    if (elapsedTime >= dur + 90 && !autoEndShownRef.current) {
      autoEndShownRef.current = true;
      if (softWrapBannerRef.current) softWrapBannerRef.current.classList.add('hidden');
      if (autoEndBannerRef.current) autoEndBannerRef.current.classList.remove('hidden');
      if (timerDisplayRef.current) {
        timerDisplayRef.current.classList.remove('text-orange-500');
        timerDisplayRef.current.classList.add('text-red-400', 'font-bold');
      }
      try {
        conversationRef.current?.sendContextualUpdate(
          `End the interview right now with: "Thank you so much, ${firstName} — this concludes our session. ` +
          `A recruiter will reach out to you shortly with next steps. Have a wonderful day!"`
        );
      } catch (e) {}
      // Give AI 5 seconds to deliver the final line, then force-end
      autoEndTimeoutRef.current = setTimeout(() => handleEndInterview('time_up'), 5000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsedTime]);

  // ── Live Coding: save code submission to backend ──
  const saveCodeToBackend = useCallback(async (code, language) => {
    if (!code || code === '(no code written)') return;
    try {
      const formData = new FormData();
      formData.append('code', code);
      formData.append('language', language);
      formData.append('passcode', interviewData?.passcode || '');
      await fetch(`${API_BASE}/api/phase3/interview/${interviewId}/save-code`, {
        method: 'POST',
        body: formData
      });
      console.log('[LiveCoding] Code saved to backend');
    } catch (e) {
      console.warn('[LiveCoding] Failed to save code to backend:', e);
    }
  }, [interviewId, interviewData]);

  // ── Tool registration confirmation ──
  useEffect(() => {
    if (callStateRef.current === 'connected') {
      console.log('✅ Client tools registered: open_code_editor, get_submitted_code');
    }
  }, [callState]);

  // Polling for pendingEditorRef is handled in CandidateInterviewPage (parent).
  // setEditorState fires in parent only → LiveInterviewRoom never re-renders.

  // ── Live Coding: notify AI when editor opens ──
  // Delayed 2s to ensure tool result fully round-trips to ElevenLabs
  // before we inject a contextual update into the same WebSocket.
  useEffect(() => {
    if (editorOpen && !codeSubmitted) {
      const timer = setTimeout(() => {
        try {
          if (callStateRef.current !== 'connected') return; // guard — don't send if disconnected
          const firstName = (interviewData?.candidate_name || '').split(' ')[0] || 'the candidate';
          conversationRef.current?.sendContextualUpdate(
            `[SYSTEM] Code editor is now open for ${firstName}. ` +
            `Language: ${editorLanguageRef.current}. ` +
            `Time limit: ${Math.round(editorTimeLimitRef.current / 60)} minutes. Candidate can now start writing.`
          );
        } catch (e) {}
      }, 3000); // 3s — contextual update after editor opens; status checked before sending
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen]);

  // ── Live Coding: editor countdown timer ──
  useEffect(() => {
    if (!editorOpen || codeSubmitted) return;
    editorTimerRef.current = setInterval(() => {
      setEditorTimeRemaining(prev => {
        const next = prev - 1;
        // Inject 2-minute warning
        if (next === 120) {
          try {
            if (callStateRef.current === 'connected') {
              conversationRef.current?.sendContextualUpdate(
                `[SYSTEM] 2 minutes remaining on the coding timer for ${interviewData?.candidate_name?.split(' ')[0] || 'the candidate'}.`
              );
            }
          } catch (e) {}
        }
        // Time expired — inject code and close editor
        if (next <= 0) {
          clearInterval(editorTimerRef.current);
          const finalCode = editorCode || '(no code written)';
          try {
            conversationRef.current?.sendContextualUpdate(
              editorClosedMessage('expired', editorLanguage, finalCode)
            );
          } catch (e) {}
          saveCodeToBackend(finalCode, editorLanguage);
          setCodeSubmitted(true);
          setEditorOpen(false);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(editorTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, codeSubmitted]);

  // ── Live Coding: notify AI when candidate starts typing ──
  useEffect(() => {
    if (!editorOpen || codeSubmitted || codingStartedRef.current) return;
    if (editorCode.length > 0) {
      codingStartedRef.current = true;
      try {
        if (callStateRef.current === 'connected') {
          conversationRef.current?.sendContextualUpdate(
            `[SYSTEM] ${interviewData?.candidate_name?.split(' ')[0] || 'The candidate'} has started writing code.`
          );
        }
      } catch (e) {}
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorCode]);

  // ── Live Coding: send code snapshot to AI every 2 minutes so it can see progress ──
  // This lets Alex ask targeted questions based on what the candidate has written.
  useEffect(() => {
    if (!editorOpen || codeSubmitted) return;
    const snapshotInterval = setInterval(() => {
      if (callStateRef.current !== 'connected') return;
      if (isSpeakingRef.current) return; // don't interrupt AI mid-speech
      const currentCode = editorCodeRef.current;
      if (!currentCode || currentCode.length < 10) return; // skip if nothing meaningful written
      if (currentCode.length > 3000) return; // skip very large code — avoid message size issues
      try {
        conversationRef.current?.sendContextualUpdate(
          `[SYSTEM] Code progress update for ${interviewData?.candidate_name?.split(' ')[0] || 'the candidate'} `
          + `(${editorLanguageRef.current}). Current code:\n\n${currentCode}\n\n`
          + `This is for your awareness only. Do NOT comment on it unless the candidate asks for help. Stay silent.`
        );
      } catch (e) {}
    }, 120000); // every 2 minutes
    return () => clearInterval(snapshotInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, codeSubmitted]);

  // ── End Interview ──
  // endReason: 'candidate_ended' (End button), 'time_up' (timer) or 'agent_closed' (allowed closing, bundle K)
  // — scoring uses it for problems never reached
  const handleEndInterview = async (endReason) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    // Bundle K: stop the turn hold and the finish flow; release the mic stream the page opened
    try { turnHoldRef.current?.stop(); } catch (e) { /* ignore */ }
    try { finishFlowRef.current?.stop(); } catch (e) { /* ignore */ }
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
    // Deploy I: report open camera episodes now, while the interview still accepts signals
    try { if (analyzerRef.current) analyzerRef.current.stop(); } catch (e) { /* never block the end */ }
    callStateRef.current = 'ended'; // Set ref FIRST — onDisconnect checks this synchronously
    setCallState('ended');
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);

    // ── If editor is still open, close it and capture whatever was written ──
    if (editorTimerRef.current) clearInterval(editorTimerRef.current);
    if (editorOpen && editorCode) {
      try {
        conversationRef.current?.sendContextualUpdate(
          `[SYSTEM] Interview ended. Final code captured (${editorLanguage}):

${editorCode}`
        );
      } catch (e) {}
    }
    setEditorOpen(false);

    try {
      // 1. Stop ElevenLabs session  2. Stop video recording
      // 3. Start the video upload straight away — alongside /end, and even without a
      //    conversation ID (the upload is not awaited; progress shows on the completion screen)
      // 4. /end (saves transcript) — needs the ElevenLabs conversation ID
      const convId = conversationIdRef.current;
      if (!convId) console.warn('⚠️ No conversation ID — skipping /end (the webhook still processes the call)');
      const { end } = await runEndSequence({
        endSession: () => conversationRef.current?.endSession(),
        stopRecording: stopVideoRecording,
        startUpload: uploadVideoBlob,
        conversationId: convId,
        callEnd: (id) => {
          const formData = new FormData();
          formData.append('conversation_id', id);
          if (['candidate_ended', 'time_up', 'agent_closed'].includes(endReason)) formData.append('end_reason', endReason);
          return fetch(`${API_BASE}/api/phase3/interview/${interviewId}/end`, { method: 'POST', body: formData });
        },
      });

      if (end) {
        const res = await end;
        if (res.ok) {
          const result = await res.json();
          console.log('✅ Interview ended successfully:', result);
        } else {
          console.warn('⚠️ End interview API returned error');
        }
      }

    } catch (e) {
      console.error('End interview error:', e);
    } finally {
      setCallState('completed');
    }
  };

  handleEndRef.current = handleEndInterview;

  // ── RENDER ──
  return (
    <div className="min-h-screen bg-gray-900 relative">
      {/* ConversationManager — never re-renders, owns the WebSocket lifetime */}
      <ConversationManager
        config={conversationConfigRef.current}
        conversationRef={conversationRef}
        isSpeakingRef={isSpeakingRef}
      />
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-3">
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <div>
            <h1 className="text-lg font-bold text-white">AI Technical Interview</h1>
            <p className="text-xs text-gray-400">{sessionData.role_type} • {sessionData.tech_stack}</p>
          </div>
          {callState === 'connected' && (
            <div className="flex items-center gap-3">
              <div ref={timerDisplayRef} className="text-sm text-white font-mono">⏱️ {formatTime(elapsedTime)}</div>
              <div className="bg-purple-600/30 text-purple-300 text-xs px-2 py-1 rounded-lg">
                {sessionData.blueprint_summary?.problem_count || '?'} problems
              </div>
              <button onClick={() => handleEndInterview('candidate_ended')}
                className="px-5 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all text-sm">
                End Interview
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Banners */}
      <div ref={warningBannerRef} className="hidden bg-yellow-500 text-white px-6 py-2.5 text-center font-semibold text-sm">
        ⚠️ 5 minutes remaining — Please wrap up your answers
      </div>
      <div ref={softWrapBannerRef} className="hidden bg-orange-500 text-white px-6 py-2.5 text-center font-semibold text-sm">
        ⏳ Time limit reached. Interview wrapping up…
      </div>
      <div ref={autoEndBannerRef} className="hidden bg-red-600 text-white px-6 py-2.5 text-center font-semibold text-sm animate-pulse">
        👋 Interview concluding. Thank you!
      </div>
      {/* Proctoring warning banner */}
      {proctoringWarning && (
        <div className="bg-orange-500 text-white px-6 py-2.5 text-center font-semibold text-sm z-50">
          {proctoringWarning}
        </div>
      )}
      {/* Network offline banner */}
      {networkOffline && (
        <div className="bg-red-700 text-white px-6 py-3 text-center font-semibold text-sm z-50 animate-pulse">
          🔴 Internet connection lost. Attempting to reconnect... Please do not close this tab.
        </div>
      )}

      {/* Main */}
      <main className="p-6">
        {callState === 'connecting' && (
          <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 120px)' }}>
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-400 border-t-transparent mx-auto mb-4"></div>
              <p className="text-white text-lg">Connecting to AI interviewer...</p>
              <p className="text-gray-400 text-sm mt-2">Generating your personalized interview questions</p>
            </div>
          </div>
        )}

        {callState === 'connected' && (
          <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 120px)' }}>
            <div className="bg-white rounded-2xl p-8 max-w-lg text-center shadow-2xl">

              {/* ── Alex Avatar ── */}
              <div className="relative inline-block mb-5">

                {/* Outer glow ring — pulses when speaking */}
                <div className={`absolute -inset-3 rounded-full transition-all duration-500 ${
                  isSpeakingRef.current
                    ? 'bg-gradient-to-br from-purple-400/40 to-indigo-500/40 animate-pulse scale-110'
                    : 'bg-gradient-to-br from-slate-200/60 to-slate-300/60'
                }`} />

                {/* Sound wave rings when speaking */}
                {isSpeakingRef.current && (
                  <>
                    <div className="absolute -inset-5 rounded-full border-2 border-purple-400/30 animate-ping" />
                    <div className="absolute -inset-8 rounded-full border border-indigo-400/20 animate-ping" style={{ animationDelay: '0.3s' }} />
                  </>
                )}

                {/* Avatar face SVG */}
                <div className="relative w-40 h-40">
                  <svg viewBox="0 0 160 160" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                    {/* Background circle */}
                    <defs>
                      <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={isSpeakingRef.current ? "#7c3aed" : "#1e40af"} />
                        <stop offset="100%" stopColor={isSpeakingRef.current ? "#4f46e5" : "#0ea5e9"} />
                      </linearGradient>
                      <linearGradient id="skinGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#fde8d0" />
                        <stop offset="100%" stopColor="#f9c9a0" />
                      </linearGradient>
                      <linearGradient id="suitGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={isSpeakingRef.current ? "#5b21b6" : "#1e3a5f"} />
                        <stop offset="100%" stopColor={isSpeakingRef.current ? "#3730a3" : "#1e40af"} />
                      </linearGradient>
                    </defs>

                    {/* Background */}
                    <circle cx="80" cy="80" r="80" fill="url(#bgGrad)" />

                    {/* Suit / body */}
                    <ellipse cx="80" cy="148" rx="52" ry="38" fill="url(#suitGrad)" />
                    {/* Shirt collar */}
                    <polygon points="68,118 80,130 92,118 88,112 80,120 72,112" fill="white" opacity="0.9" />
                    {/* Tie */}
                    <polygon points="78,118 82,118 84,138 80,142 76,138" fill={isSpeakingRef.current ? "#a78bfa" : "#60a5fa"} />

                    {/* Neck */}
                    <rect x="73" y="106" width="14" height="16" rx="4" fill="url(#skinGrad)" />

                    {/* Head */}
                    <ellipse cx="80" cy="82" rx="34" ry="36" fill="url(#skinGrad)" />

                    {/* Hair */}
                    <ellipse cx="80" cy="52" rx="34" ry="14" fill="#1a1a2e" />
                    <ellipse cx="80" cy="46" rx="30" ry="10" fill="#2d2d44" />
                    {/* Hair side */}
                    <ellipse cx="47" cy="72" rx="7" ry="18" fill="#1a1a2e" />
                    <ellipse cx="113" cy="72" rx="7" ry="18" fill="#1a1a2e" />

                    {/* Ears */}
                    <ellipse cx="46" cy="84" rx="5" ry="7" fill="#f0c080" />
                    <ellipse cx="114" cy="84" rx="5" ry="7" fill="#f0c080" />

                    {/* Eyes */}
                    <ellipse cx="67" cy="80" rx="6" ry="7" fill="white" />
                    <ellipse cx="93" cy="80" rx="6" ry="7" fill="white" />
                    {/* Pupils — look slightly forward */}
                    <circle cx="68" cy="81" r="3.5" fill="#1a1a2e" />
                    <circle cx="94" cy="81" r="3.5" fill="#1a1a2e" />
                    {/* Eye shine */}
                    <circle cx="69.5" cy="79.5" r="1.2" fill="white" />
                    <circle cx="95.5" cy="79.5" r="1.2" fill="white" />

                    {/* Eyebrows */}
                    <path d="M61 72 Q67 69 73 71" stroke="#1a1a2e" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                    <path d="M87 71 Q93 69 99 72" stroke="#1a1a2e" strokeWidth="2.5" fill="none" strokeLinecap="round" />

                    {/* Nose */}
                    <path d="M78 86 Q76 92 80 94 Q84 92 82 86" stroke="#d4956a" strokeWidth="1.5" fill="none" strokeLinecap="round" />

                    {/* Mouth — animated based on speaking state */}
                    {isSpeakingRef.current ? (
                      // Open mouth when speaking
                      <>
                        <path d="M69 101 Q80 110 91 101" stroke="#c0705a" strokeWidth="2" fill="#c0705a" />
                        <ellipse cx="80" cy="104" rx="8" ry="5" fill="#8b2020" />
                        <ellipse cx="80" cy="104" rx="6" ry="3" fill="#c0705a" />
                        {/* Teeth */}
                        <rect x="74" y="100" width="12" height="4" rx="2" fill="white" />
                      </>
                    ) : (
                      // Slight smile when listening
                      <path d="M70 100 Q80 108 90 100" stroke="#c0705a" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                    )}

                    {/* Glasses */}
                    <rect x="58" y="74" width="20" height="14" rx="5" fill="none" stroke={isSpeakingRef.current ? "#a78bfa" : "#94a3b8"} strokeWidth="2" />
                    <rect x="82" y="74" width="20" height="14" rx="5" fill="none" stroke={isSpeakingRef.current ? "#a78bfa" : "#94a3b8"} strokeWidth="2" />
                    <line x1="78" y1="81" x2="82" y2="81" stroke={isSpeakingRef.current ? "#a78bfa" : "#94a3b8"} strokeWidth="2" />
                    <line x1="46" y1="81" x2="58" y2="81" stroke={isSpeakingRef.current ? "#a78bfa" : "#94a3b8"} strokeWidth="1.5" />
                    <line x1="102" y1="81" x2="112" y2="81" stroke={isSpeakingRef.current ? "#a78bfa" : "#94a3b8"} strokeWidth="1.5" />

                    {/* Microphone badge when speaking */}
                    {isSpeakingRef.current && (
                      <circle cx="128" cy="128" r="14" fill="#7c3aed" stroke="white" strokeWidth="2" />
                    )}
                    {isSpeakingRef.current && (
                      <text x="128" y="133" textAnchor="middle" fontSize="14">🎙</text>
                    )}
                  </svg>
                </div>

                {/* Name badge */}
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-full px-3 py-0.5 shadow-sm">
                  <span className="text-xs font-bold text-gray-700">Alex</span>
                  <span className="text-xs text-gray-400 ml-1">AI Interviewer</span>
                </div>
              </div>

              {/* Sound bars when speaking */}
              {isSpeakingRef.current && (
                <div className="flex items-end justify-center gap-1 h-8 mb-4">
                  {[3,5,8,5,9,4,7,5,3,6,8,4,6].map((h, i) => (
                    <div key={i}
                      className="w-1.5 bg-purple-500 rounded-full"
                      style={{
                        height: `${h * 3}px`,
                        animation: `soundBar 0.${4 + (i % 4)}s ease-in-out infinite alternate`,
                        animationDelay: `${i * 0.07}s`
                      }}
                    />
                  ))}
                </div>
              )}

              <h2 className="text-xl font-bold text-gray-900 mb-1 mt-4">Interview In Progress</h2>

              <div className={`flex items-center justify-center gap-2 mb-5 transition-colors ${
                isSpeakingRef.current ? 'text-purple-600' : 'text-green-600'
              }`}>
                <div className={`w-2.5 h-2.5 rounded-full ${
                  isSpeakingRef.current ? 'bg-purple-500 animate-ping' : 'bg-green-500 animate-pulse'
                }`} />
                <span className="font-semibold text-sm">
                  {isSpeakingRef.current ? 'Alex is speaking...' : '🎤 Your turn — go ahead'}
                </span>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 text-left text-sm text-gray-500 space-y-1.5">
                <p>• Speak clearly — take your time to think</p>
                <p>• Ask Alex for clarification if needed</p>
                <p>• Use the 💬 icon to see the live transcript</p>
                <p>• Do not switch browser tabs (monitored)</p>
              </div>

              {/* CSS for sound bars */}
              <style>{`
                @keyframes soundBar {
                  from { transform: scaleY(0.4); opacity: 0.6; }
                  to   { transform: scaleY(1.2); opacity: 1; }
                }
              `}</style>
            </div>

            {/* ── Live Coding Editor Panel ── */}
            {interviewData?.coding_mode === 'live_coding' && editorOpen && (
              <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                <div className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden border border-purple-500/40"
                  style={{ maxHeight: '90vh' }}>

                  {/* Editor header */}
                  <div className="flex items-center justify-between px-5 py-3 bg-gray-800 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">💻</span>
                      <span className="text-white font-bold text-sm">Code Editor</span>
                      <span className="bg-purple-600/40 text-purple-300 text-xs px-2 py-0.5 rounded-full font-mono">{editorLanguage}</span>
                    </div>
                    {/* Countdown timer */}
                    <div className={`font-mono text-sm font-bold px-3 py-1 rounded-lg ${
                      editorTimeRemaining <= 120 ? 'bg-red-500/20 text-red-400 animate-pulse'
                      : editorTimeRemaining <= 300 ? 'bg-yellow-500/20 text-yellow-400'
                      : 'bg-green-500/20 text-green-400'
                    }`}>
                      ⏱ {Math.floor(editorTimeRemaining / 60)}:{String(editorTimeRemaining % 60).padStart(2, '0')}
                    </div>
                  </div>

                  {/* Language selector */}
                  <div className="px-5 py-2 bg-gray-850 border-b border-gray-700 flex items-center gap-3">
                    <span className="text-gray-400 text-xs">Language:</span>
                    {['python', 'javascript', 'java', 'c++', 'c#', 'go', 'sql', 'other'].map(lang => (
                      <button key={lang}
                        onClick={() => {
                          setEditorLanguage(lang);
                          if (lang !== editorLanguage && callStateRef.current === 'connected') {
                            try {
                              conversationRef.current?.sendContextualUpdate(
                                `[SYSTEM] Candidate switched coding language from ${editorLanguage} to ${lang}. `
                                + `Adjust your follow-up questions accordingly.`
                              );
                            } catch (e) {}
                          }
                        }}
                        className={`text-xs px-2.5 py-1 rounded-lg font-mono transition-all ${
                          editorLanguage === lang
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}>
                        {lang}
                      </button>
                    ))}
                  </div>

                  {/* Code textarea */}
                  <textarea
                    className="flex-1 bg-gray-950 text-green-300 font-mono text-sm p-5 resize-none outline-none placeholder-gray-600"
                    style={{ minHeight: '340px' }}
                    placeholder={`// Write your ${editorLanguage} solution here...
// Speak to the AI if you need clarification`}
                    value={editorCode}
                    onChange={e => setEditorCode(e.target.value)}
                    spellCheck={false}
                    autoCorrect="off"
                    autoCapitalize="off"
                  />

                  {/* Footer */}
                  <div className="px-5 py-3 bg-gray-800 border-t border-gray-700 flex items-center justify-between">
                    <p className="text-gray-400 text-xs">
                      {editorCode.length === 0
                        ? '🎤 Speak to the AI if you need clarification on the problem'
                        : `${editorCode.split('\n').length} lines · ${editorCode.length} chars`}
                    </p>
                    <button
                      onClick={() => {
                        clearInterval(editorTimerRef.current);
                        const finalCode = editorCode || '(no code written)';
                        try {
                          conversationRef.current?.sendContextualUpdate(
                            editorClosedMessage('submitted', editorLanguage, finalCode)
                          );
                        } catch (e) {}
                        saveCodeToBackend(finalCode, editorLanguage);
                        setCodeSubmitted(true);
                        setEditorOpen(false);
                      }}
                      className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-xl transition-all">
                      ✅ Submit Solution
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Camera PiP — bottom-left, always visible during interview */}
            <div className="fixed bottom-6 left-6 z-40">
              <div className="relative w-48 h-36 bg-black rounded-xl overflow-hidden border-2 border-gray-600 shadow-2xl">
                <video ref={videoPreviewRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                {/* Recording indicator */}
                <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 px-2 py-0.5 rounded-full">
                  <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-white text-xs font-semibold">REC</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {(callState === 'ended' || callState === 'completed') && (
          <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 px-4 py-10">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">

              {/* ── Top branded header ── */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-8 py-7 text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-white mb-1">Interview Complete</h2>
                <p className="text-purple-200 text-sm">
                  {interviewData?.role_type || 'Technical Screen'} · {interviewData?.interview_level || ''}
                </p>
              </div>

              <div className="px-8 py-7">

                {/* ── Candidate name + thank you ── */}
                <div className="text-center mb-6">
                  <p className="text-gray-800 font-semibold text-lg">
                    Thank you, {(interviewData?.candidate_name || '').split(' ')[0] || 'Candidate'}!
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    Your interview has been successfully recorded and submitted.
                  </p>
                </div>

                {/* ── Saving indicator ── */}
                {callState === 'ended' && (
                  <div className="flex items-center justify-center gap-2 text-purple-600 mb-5 bg-purple-50 rounded-xl py-2.5">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent"></div>
                    <span className="text-sm font-medium">Saving your session...</span>
                  </div>
                )}

                {/* ── Video upload status ── */}
                {videoUpload.status === 'uploading' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5" role="status">
                    <p className="text-sm font-semibold text-amber-800 text-center">
                      Uploading your recording — please keep this window open
                    </p>
                    <div className="h-2 bg-amber-100 rounded-full overflow-hidden mt-2.5">
                      <div className="h-full bg-amber-500 rounded-full transition-all duration-300"
                        style={{ width: `${videoUpload.percent}%` }}></div>
                    </div>
                    <p className="text-xs text-amber-700 text-center mt-1.5">
                      {videoUpload.percent}%{videoUpload.retry > 0 ? ` · retrying (${videoUpload.retry} of 3)` : ''}
                    </p>
                  </div>
                )}
                {videoUpload.status === 'done' && (
                  <div className="bg-green-50 border border-green-200 rounded-xl py-2.5 mb-5 text-center">
                    <span className="text-sm font-medium text-green-700">✅ Recording uploaded</span>
                  </div>
                )}
                {videoUpload.status === 'failed' && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-2.5 mb-5 text-center">
                    <span className="text-sm text-orange-800">
                      We couldn't upload your video recording. Your interview answers were saved, and your recruiter has been notified.
                    </span>
                  </div>
                )}

                {/* ── What happens next ── */}
                <div className="bg-gray-50 rounded-2xl p-5 mb-6">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">What happens next</p>
                  <div className="space-y-4">
                    {[
                      { icon: '📊', title: 'AI Scoring', desc: 'Your responses are being scored across technical dimensions' },
                      { icon: '📋', title: 'Recruiter Review', desc: 'Your recruiter will review the detailed assessment report' },
                      { icon: '📧', title: 'Feedback Coming Soon', desc: 'You will be contacted with feedback on the next steps' },
                    ].map((step, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0 text-base">{step.icon}</div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{step.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── Candidate feedback ── */}
                {!feedbackSubmitted ? (
                  <div className="border border-gray-200 rounded-2xl p-5 mb-5">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Rate Your Experience</p>
                    {/* Star rating */}
                    <div className="flex items-center justify-center gap-2 mb-4">
                      {[1,2,3,4,5].map(star => (
                        <button
                          key={star}
                          onClick={() => setFeedbackRating(star)}
                          className={`text-3xl transition-all hover:scale-110 ${
                            star <= feedbackRating ? 'text-yellow-400' : 'text-gray-200'
                          }`}>
                          ★
                        </button>
                      ))}
                    </div>
                    {feedbackRating > 0 && (
                      <div className="text-center text-xs text-gray-500 mb-3">
                        {['','Poor','Fair','Good','Very Good','Excellent'][feedbackRating]}
                      </div>
                    )}
                    <textarea
                      value={feedbackComment}
                      onChange={e => setFeedbackComment(e.target.value)}
                      placeholder="Any comments about your experience? (optional)"
                      rows={3}
                      className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 resize-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-gray-700 placeholder-gray-400"
                    />
                    <button
                      onClick={async () => {
                        if (!feedbackRating) return;
                        setFeedbackSubmitting(true);
                        try {
                          const fd = new FormData();
                          fd.append('passcode', interviewData?.passcode || '');
                          fd.append('rating', feedbackRating);
                          fd.append('comment', feedbackComment);
                          const fbRes = await fetch(`${API_BASE}/api/phase3/interview/${interviewId}/candidate-feedback`, {
                            method: 'POST', body: fd
                          });
                          if (fbRes.ok || fbRes.status === 404) setFeedbackSubmitted(true);
                          else setFeedbackSubmitted(true); // always complete for UX
                        } catch (e) {
                          setFeedbackSubmitted(true); // don't block on error
                        } finally {
                          setFeedbackSubmitting(false);
                        }
                      }}
                      disabled={!feedbackRating || feedbackSubmitting}
                      className={`w-full mt-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        feedbackRating && !feedbackSubmitting
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}>
                      {feedbackSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </button>
                  </div>
                ) : (
                  <div className="border border-green-200 bg-green-50 rounded-2xl p-4 mb-5 text-center">
                    <span className="text-green-600 font-semibold text-sm">✅ Thank you for your feedback!</span>
                  </div>
                )}

                {/* ── Close instruction — only once nothing is still uploading ── */}
                {videoUpload.status === 'uploading' || callState === 'ended' ? (
                  <p className="text-center text-xs font-semibold text-amber-700">
                    {videoUpload.status === 'uploading'
                      ? 'Please keep this window open until your recording has finished uploading.'
                      : 'Please keep this window open while we save your session.'}
                  </p>
                ) : (
                  <p className="text-center text-xs text-gray-400">
                    You may safely close this window. Good luck! 🍀
                  </p>
                )}

              </div>
            </div>
          </div>
        )}

        {callState === 'error' && (
          <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 120px)' }}>
            <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Connection Issue</h2>
              <p className="text-gray-600 mb-6">{error || 'The connection to the AI interviewer was lost.'}</p>
              <button onClick={() => window.location.reload()}
                className="w-full py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-all">
                🔄 Try Reconnecting
              </button>
              <p className="text-xs text-gray-400 mt-4">If the issue persists, contact your recruiter.</p>
            </div>
          </div>
        )}
      </main>

      {/* ── Floating Chat Transcript ── */}
      {(callState === 'connected' || ((callState === 'ended' || callState === 'completed') && chatMessages.length > 0)) && (
        <>
          <button onClick={() => setIsChatOpen(!isChatOpen)}
            className="fixed bottom-6 right-6 w-14 h-14 bg-purple-600 hover:bg-purple-700 text-white rounded-full shadow-2xl flex items-center justify-center transition-all z-50 hover:scale-110"
            title="Toggle Transcript">
            {isChatOpen ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            )}
            {!isChatOpen && unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center animate-bounce">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {isChatOpen && (
            <div className="fixed bottom-24 right-6 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col z-50 overflow-hidden"
              style={{ maxHeight: '60vh', animation: 'slideUp 0.2s ease-out' }}>
              <div className="bg-gradient-to-r from-purple-600 to-indigo-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-lg">💬</span>
                  <h3 className="text-white font-bold text-sm">Live Transcript</h3>
                  <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">{chatMessages.length}</span>
                </div>
                <button onClick={() => setIsChatOpen(false)} className="text-purple-200 hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.length === 0 ? (
                  <div className="text-center py-8">
                    <span className="text-4xl">🎙️</span>
                    <p className="text-gray-400 text-sm mt-2">Conversation will appear here...</p>
                  </div>
                ) : chatMessages.map(msg => (
                  <div key={msg.id} className={`flex ${msg.source === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] ${
                      msg.source === 'user'
                        ? 'bg-purple-600 text-white rounded-2xl rounded-br-sm'
                        : 'bg-gray-100 text-gray-800 rounded-2xl rounded-bl-sm'
                    } px-4 py-2.5 shadow-sm`}>
                      <p className={`text-xs font-bold mb-0.5 ${msg.source === 'user' ? 'text-purple-200' : 'text-gray-500'}`}>
                        {msg.source === 'ai' ? '🤖 AI Interviewer' : '👤 You'}
                      </p>
                      <p className="text-sm leading-relaxed">{msg.message}</p>
                      <p className={`text-xs mt-1 ${msg.source === 'user' ? 'text-purple-200' : 'text-gray-400'}`}>
                        {formatChatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            </div>
          )}

          <style>{`
            @keyframes slideUp {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT: STATE MACHINE
// ═══════════════════════════════════════════════════════════════

function CandidateInterviewPage() {
  const { interviewId } = useParams();
  const [phase, setPhase] = useState('passcode');  // passcode → lobby → starting → live → done
  const [interviewData, setInterviewData] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [selectedMic, setSelectedMic] = useState('');
  const [selectedSpeaker, setSelectedSpeaker] = useState('');
  const [selectedCamera, setSelectedCamera] = useState('');
  const [cameraAnalysis, setCameraAnalysis] = useState({ consent: false, baselinePitch: null });   // Deploy I
  const [startError, setStartError] = useState('');

  // ── Editor state lives HERE so setEditorState never re-renders LiveInterviewRoom ──
  // LiveInterviewRoom + ConversationManager are both memoized/stable.
  // When editor opens, only CandidateInterviewPage re-renders — WebSocket unaffected.
  const [editorState, setEditorState] = useState({ open: false, code: '', language: 'python', timeRemaining: 0, submitted: false });
  const pendingEditorRef = useRef(null); // set by open_code_editor tool; polled here

  // Poll pendingEditorRef — opens editor in parent, never touches LiveInterviewRoom
  useEffect(() => {
    if (phase !== 'live') return;
    const poll = setInterval(() => {
      if (pendingEditorRef.current) {
        const { lang, limit, requestedAt } = pendingEditorRef.current;
        pendingEditorRef.current = null;
        const delay = Date.now() - requestedAt;
        console.log(`[Editor] Opening after ${delay}ms — WebRTC unaffected`);
        setEditorState({ open: true, code: '', language: lang, timeRemaining: limit, submitted: false });
      }
    }, 500);
    return () => clearInterval(poll);
  }, [phase]);

  const handlePasscodeVerified = (data) => {
    setInterviewData(data);
    setPhase('lobby');
  };

  const handleStartInterview = async (mic, speaker, camera, cameraAnalysisChoice) => {
    setCameraAnalysis(cameraAnalysisChoice || { enabled: false, consent: false, baselinePitch: null });
    setSelectedMic(mic);
    setSelectedSpeaker(speaker);
    setSelectedCamera(camera);
    setPhase('starting');
    setStartError('');

    try {
      const formData = new FormData();
      formData.append('passcode', interviewData.passcode);
      formData.append('camera_analysis_consent', cameraAnalysisChoice?.consent ? 'true' : 'false');

      const res = await fetch(`${API_BASE}/api/phase3/interview/${interviewId}/start`, {
        method: 'POST',
        body: formData
      });

      const data = await res.json();

      if (res.ok && data.status === 'success') {
        setSessionData({ ...data.data, _receivedAtMs: Date.now() });   // timer anchor (see interviewTiming.js)
        setPhase('live');
      } else {
        setStartError(data.detail || 'Failed to start interview. Please try again.');
        setPhase('lobby');
      }
    } catch (e) {
      setStartError('Connection error. Please check your internet and try again.');
      setPhase('lobby');
    }
  };

  // ── Passcode phase ──
  if (phase === 'passcode') {
    return <PasscodeEntry interviewId={interviewId} onVerified={handlePasscodeVerified} />;
  }

  // ── Lobby phase ──
  if (phase === 'lobby') {
    return (
      <>
        {startError && (
          <div className="fixed top-0 left-0 right-0 bg-red-600 text-white text-center py-3 text-sm font-semibold z-50">
            ⚠️ {startError}
          </div>
        )}
        <PreInterviewLobby interviewData={interviewData} onStartInterview={handleStartInterview} />
      </>
    );
  }

  // ── Starting phase (generating blueprint) ──
  if (phase === 'starting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block mb-6">
            <div className="w-24 h-24 border-4 border-purple-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl">🧠</span>
            </div>
          </div>
          <p className="text-white text-xl font-bold">Preparing Your Interview</p>
          <p className="text-gray-400 text-sm mt-2">Soon you will joined with an AI Interviewer...</p>
          <p className="text-gray-500 text-xs mt-1">This may take 1-2 minutes</p>
        </div>
      </div>
    );
  }

  // ── Live interview phase ──
  if (phase === 'live' && sessionData) {
    return (
      <LiveInterviewRoom
        interviewId={interviewId}
        interviewData={interviewData}
        sessionData={sessionData}
        selectedMic={selectedMic}
        selectedSpeaker={selectedSpeaker}
        selectedCamera={selectedCamera}
        cameraAnalysis={cameraAnalysis}
        editorState={editorState}
        setEditorState={setEditorState}
        pendingEditorRef={pendingEditorRef}
      />
    );
  }

  return null;
}

export default CandidateInterviewPage;